import { createContext, useContext, useState, useCallback } from 'react'
import { CheckCircle, XCircle, Info, X } from 'lucide-react'
import { TOAST_DURATION_MS } from '@/lib/constants'

const ToastContext = createContext(null)

let toastId = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  /** Add a toast. Two call shapes (back-compat):
   *
   *   addToast('Saved', 'success')                              // string-only legacy
   *   addToast('Failed to save', 'error', { actionLabel: 'Retry', onAction: handleRetry })
   *
   * When `actionLabel` + `onAction` are supplied, the toast renders an
   * action button (e.g. "Retry") next to the dismiss X. Tapping the
   * action runs `onAction` and dismisses the toast. */
  const addToast = useCallback(
    (message, type = 'info', opts = {}) => {
      const { actionLabel, onAction, durationMs } = opts
      const id = ++toastId
      setToasts(prev => [...prev, { id, message, type, actionLabel, onAction }])
      // Errors with an action button get a longer dwell so users can read
      // + decide whether to retry. Pass durationMs to override.
      const ttl = durationMs ?? (actionLabel ? TOAST_DURATION_MS * 2 : TOAST_DURATION_MS)
      setTimeout(() => removeToast(id), ttl)
      return id
    },
    [removeToast]
  )

  const toast = {
    success: msg => addToast(msg, 'success'),
    error: (msg, opts) => addToast(msg, 'error', opts),
    info: msg => addToast(msg, 'info'),
  }

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast container — aria-live region so screen readers announce toasts.
          WCAG 2.2 SC 4.1.3 (Status Messages). The container itself uses
          aria-live="polite" so info/success toasts wait for the user to
          finish reading; individual error toasts get role="alert" inline
          which screen readers treat as assertive (interrupt). aria-atomic
          ensures the whole toast text is read, not just the diff. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map(t => (
          <div
            key={t.id}
            role={t.type === 'error' ? 'alert' : undefined}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-[slideUp_0.3s_ease-out] max-w-sm ${
              t.type === 'success'
                ? 'bg-emerald-900/90 text-emerald-100 border border-emerald-700/50'
                : t.type === 'error'
                  ? 'bg-red-900/90 text-red-100 border border-red-700/50'
                  : 'bg-midnight/95 text-warm-cream border border-white/10'
            }`}
          >
            {t.type === 'success' && (
              <CheckCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
            )}
            {t.type === 'error' && <XCircle className="w-4 h-4 shrink-0" aria-hidden="true" />}
            {t.type === 'info' && <Info className="w-4 h-4 shrink-0" aria-hidden="true" />}
            <span className="flex-1">{t.message}</span>
            {t.actionLabel && t.onAction && (
              <button
                onClick={() => {
                  t.onAction()
                  removeToast(t.id)
                }}
                className="shrink-0 px-2 py-0.5 rounded-md bg-white/10 hover:bg-white/20 text-xs font-semibold transition-colors"
              >
                {t.actionLabel}
              </button>
            )}
            <button
              onClick={() => removeToast(t.id)}
              className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss notification"
            >
              <X className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
